import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { RecipeInput, type RecipeInputMode } from "@/components/RecipeInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppHeader } from "@/components/AppHeader";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState, type CleanError } from "@/components/ErrorState";
// GuidedCrop is only mounted *after* the user uploads an image and chooses to
// mark regions, so lazy-loading it keeps the (large) component out of the
// initial landing-page bundle.
const GuidedCrop = lazy(() =>
  import("@/components/GuidedCrop").then((m) => ({ default: m.GuidedCrop })),
);
import { PhotoTray, type TrayItem } from "@/components/PhotoTray";
import { HeroVisual } from "@/components/landing/HeroVisual";
import {
  Problem,
  Solution,
  Showcase,
  Benefits,
  SaveOrganise,
  SocialProof,
  FinalCTA,
} from "@/components/landing/LandingSections";
import { WelcomeHero } from "@/components/onboarding/WelcomeHero";
import { hasFlag, setFlag } from "@/lib/onboarding";
import { supabase } from "@/integrations/supabase/client";
import type { CleanRecipe } from "@/types/recipe";
import { isMobile } from "@/lib/device";
// pdfToImages pulls in pdfjs-dist (~200 KiB). It's only needed when the user
// uploads a PDF, so dynamic-import inside the handler keeps it out of the
// landing-page bundle entirely.
import { processImage, blobToDataUrl } from "@/lib/imageProcess";
import { uploadRecipeImage, UploadError } from "@/lib/imageUpload";
import {
  clearPersistedAttempt,
  dataUrlToFile,
  loadPersistedAttempt,
  savePersistedAttempt,
} from "@/lib/uploadPersistence";
import { toast } from "sonner";
import { logErrorEvent } from "@/lib/logError";
import { friendlyCleanRecipeError } from "@/lib/cleanRecipeError";
import { usePreferences } from "@/hooks/usePreferences";
import { dietLabel } from "@/lib/preferences";

type LastAttempt =
  | { type: "url"; url: string }
  | { type: "text"; text: string }
  | {
      type: "image";
      files: File[];
      dataUrls: string[];
      /** Indexed by file position. Filled in as each upload succeeds so a
       *  retry can resume from the first failure instead of redoing work. */
      uploadedUrls: (string | undefined)[];
    };

type StagedItem = {
  id: string;
  file: File;
  dataUrl: string;
};

const MAX_IMAGES = 8;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

let __idCounter = 0;
const newId = () => `staged-${Date.now()}-${++__idCounter}`;

const Index = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const captureMode = searchParams.get("capture") === "1";
  const { prefs } = usePreferences();
  const dietPreferenceLabel = useMemo(() => dietLabel(prefs.diet), [prefs.diet]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | undefined>();
  const [uploadProgress, setUploadProgress] = useState<number | undefined>();
  const [error, setError] = useState<CleanError | null>(null);
  const [lastAttempt, setLastAttempt] = useState<LastAttempt | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [staged, setStaged] = useState<StagedItem[]>([]);
  const [cameraBatch, setCameraBatch] = useState(false);
  // When the user hits "Paste the recipe text instead" from a URL error,
  // we force the RecipeInput to open on its text tab on the next render.
  const [forcedMode, setForcedMode] = useState<RecipeInputMode | undefined>();
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const pdfRef = useRef<HTMLInputElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  // Track the signed-in state so we can hide the marketing landing
  // sections for returning users — they want a fast capture surface, not
  // another value-prop pitch. Anonymous visitors still see the full
  // landing page.
  // Initialised to `false` (not `null`) so the first paint matches the
  // post-auth layout for the common case of an anonymous visitor — this
  // prevents WelcomeHero mounting late and pushing the hero section down
  // by ~1100px, which was producing a 0.85 CLS on the landing page.
  const [signedIn, setSignedIn] = useState<boolean>(false);
  // First-visit welcome takeover: shown only to anonymous visitors who
  // haven't dismissed it before. Signed-in users (and returning anon users)
  // skip straight to today's behaviour.
  const [showWelcome, setShowWelcome] = useState<boolean>(() => !hasFlag("onboarded"));
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setSignedIn(!!session?.user?.email);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSignedIn(!!session?.user?.email);
    });
    return () => sub.subscription.unsubscribe();
  }, []);
  // Once signed in, the welcome screen is irrelevant — never show it.
  useEffect(() => {
    if (signedIn) setShowWelcome(false);
  }, [signedIn]);

  const scrollToHero = () => {
    heroRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };


  const mobile = useMemo(() => isMobile(), []);

  const parseFunctionError = async (
    err: any,
  ): Promise<{ message: string; code?: string }> => {
    let rawCode: string | undefined;
    let rawMessage: string | undefined;
    const ctx = err?.context;
    if (ctx) {
      try {
        const cloned = typeof ctx.clone === "function" ? ctx.clone() : ctx;
        const text = typeof cloned.text === "function" ? await cloned.text() : "";
        if (text) {
          try {
            const body = JSON.parse(text);
            if (body?.error) {
              rawMessage = body.error;
              rawCode = body.code;
            }
          } catch {
            rawMessage = text;
          }
        }
      } catch {
        // fall through
      }
    }
    if (!rawMessage && err?.message) rawMessage = err.message;
    // Translate to friendly copy. We log the raw signal for internal
    // debugging but never surface it to the user.
    if (rawCode || rawMessage) {
      logErrorEvent({
        code: rawCode || "clean_recipe_error",
        source: "url",
        message: rawMessage,
      });
    }
    const friendly = friendlyCleanRecipeError({
      code: rawCode,
      message: rawMessage,
      cause: err,
    });
    return { message: friendly, code: rawCode };
  };

  const goToRecipe = (recipe: CleanRecipe) => {
    clearPersistedAttempt();
    // Clear the saved-share-id marker so a freshly-cleaned recipe starts in
    // the unsaved state. Without this, the previous recipe's share id leaks
    // through and the Save button renders as "Saved" before the user has
    // tapped anything (and the recipe never actually gets persisted).
    sessionStorage.removeItem("cp_recipe_saved_share_id");
    sessionStorage.setItem("cp_recipe", JSON.stringify(recipe));
    // Mark the first-clean milestone so the result page can show the
    // first-time headline + behavioural nudge. Idempotent — only matters
    // the very first time this fires.
    setFlag("firstClean");
    navigate("/recipe");
  };

  /** Persist the current image-attempt state to sessionStorage. */
  const persistAttempt = (
    files: File[],
    dataUrls: string[],
    uploadedUrls: (string | undefined)[],
    err: CleanError | null,
    attempt: number,
  ) => {
    savePersistedAttempt({
      dataUrls,
      uploadedUrls: uploadedUrls.map((u) => u ?? null),
      filenames: files.map((f) => f.name),
      error: err,
      retryCount: attempt,
      savedAt: Date.now(),
    });
  };

  // Restore an in-flight upload attempt after a refresh. We rebuild File
  // objects from the persisted dataUrls so retry can re-upload anything that
  // wasn't already on the server.
  useEffect(() => {
    const persisted = loadPersistedAttempt();
    if (!persisted) return;
    const files = persisted.dataUrls.map((d, i) =>
      dataUrlToFile(d, persisted.filenames[i] || `photo-${i + 1}.jpg`),
    );
    const uploadedUrls = persisted.uploadedUrls.map((u) => u ?? undefined);
    setLastAttempt({
      type: "image",
      files,
      dataUrls: persisted.dataUrls,
      uploadedUrls,
    });
    setRetryCount(persisted.retryCount);
    if (persisted.error) {
      setError(persisted.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fileToDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const stubRecipe = (sourceUrl?: string): CleanRecipe => ({
    title: "",
    prep_time: "",
    cook_time: "",
    servings: "",
    tags: [],
    ingredient_groups: [{ name: "Ingredients", items: [""] }],
    method: [""],
    notes: "We couldn't read this clearly — please fill in the recipe below.",
    image_url: "",
    source_url: sourceUrl,
    best_effort: true,
  });

  const callClean = async (
    body: Record<string, unknown>,
  ): Promise<{ recipe?: CleanRecipe; error?: CleanError }> => {
    // Pass the user's diet preference to the cleaner so the AI can apply a
    // diet tag when confidence is high. Pure best-effort — never blocks the
    // request, never injected when no preference is set.
    const bodyWithDiet = dietPreferenceLabel
      ? { ...body, dietPreference: dietPreferenceLabel }
      : body;
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "clean-recipe",
        { body: bodyWithDiet },
      );
      if (invokeError) {
        const { message, code } = await parseFunctionError(invokeError);
        return {
          error: { message, code, source: "url", sourceLabel: "" },
        };
      }
      if (data?.error) {
        return {
          error: { message: data.error, code: data.code, source: "url", sourceLabel: "" },
        };
      }
      return { recipe: data?.recipe };
    } catch (err: any) {
      const { message, code } = await parseFunctionError(err);
      return {
        error: { message, code, source: "url", sourceLabel: "" },
      };
    }
  };

  const cleanUrl = async (u: string, attempt = 0) => {
    setLoading(true);
    setLoadingMessage(undefined);
    setError(null);
    setLastAttempt({ type: "url", url: u });
    setRetryCount(attempt);

    const bestEffort = attempt >= 2;
    const { recipe, error: err } = await callClean(
      bestEffort ? { url: u, best_effort: true } : { url: u },
    );

    if (recipe) {
      goToRecipe(recipe);
      return;
    }
    if (bestEffort) {
      goToRecipe(stubRecipe(u));
      return;
    }
    setError({ ...err!, source: "url", sourceLabel: u });
    setLoading(false);
  };

  const cleanText = async (raw: string, attempt = 0) => {
    const text = raw.trim();
    if (!text) return;
    setLoading(true);
    setLoadingMessage("Reading your notes…");
    setError(null);
    setLastAttempt({ type: "text", text });
    setRetryCount(attempt);

    const bestEffort = attempt >= 2;
    const body: Record<string, unknown> = { text };
    if (bestEffort) body.best_effort = true;

    const { recipe, error: err } = await callClean(body);

    if (recipe) {
      goToRecipe(recipe);
      return;
    }
    if (bestEffort) {
      goToRecipe(stubRecipe());
      return;
    }
    const label =
      text.length > 60 ? `${text.slice(0, 57).trim()}…` : text;
    setError({ ...err!, source: "text", sourceLabel: label });
    setLoading(false);
  };

  const cleanImage = async (
    files: File[],
    dataUrls: string[],
    attempt = 0,
    /** Pre-uploaded URLs from a prior failed attempt; same length as files,
     *  with `undefined` slots for items that still need uploading. */
    priorUploaded: (string | undefined)[] = [],
  ) => {
    setLoading(true);
    setLoadingMessage(
      files.length > 1 ? `Uploading ${files.length} photos…` : "Uploading your photo…",
    );
    setUploadProgress(0);
    setError(null);
    // Track the upload state on lastAttempt so the retry button can resume.
    const uploadedUrls: (string | undefined)[] = files.map(
      (_, i) => priorUploaded[i],
    );
    setLastAttempt({ type: "image", files, dataUrls, uploadedUrls: [...uploadedUrls] });
    setRetryCount(attempt);
    persistAttempt(files, dataUrls, uploadedUrls, null, attempt);

    try {
      const totalCount = files.length;
      for (let i = 0; i < files.length; i++) {
        if (uploadedUrls[i]) continue; // resume — skip already-uploaded files
        const file = files[i];
        if (totalCount > 1) {
          setLoadingMessage(`Uploading photo ${i + 1} of ${totalCount}…`);
        }
        const url = await uploadRecipeImage(file, "draft", (p) => {
          // Combine completed files + current file fraction across the batch.
          // Indeterminate (-1) on the active file → show indeterminate overall.
          if (p.fraction < 0) {
            setUploadProgress(-1);
            return;
          }
          const completed = uploadedUrls.filter(Boolean).length;
          const overall = (completed + p.fraction) / totalCount;
          setUploadProgress(overall);
        });
        uploadedUrls[i] = url;
        // Persist progress so a mid-batch failure preserves what succeeded —
        // both in memory (for in-tab retry) and in sessionStorage (for refresh).
        setLastAttempt({
          type: "image",
          files,
          dataUrls,
          uploadedUrls: [...uploadedUrls],
        });
        persistAttempt(files, dataUrls, uploadedUrls, null, attempt);
      }
      setUploadProgress(1);
    } catch (err) {
      console.error("image upload failed", err);
      const label = files.length === 1 ? files[0].name : `${files.length} photos`;
      const uploadErr = err instanceof UploadError ? err : null;
      const code = uploadErr?.code ?? "upload_failed";
      const message =
        uploadErr?.message ??
        (err instanceof Error ? err.message : "We couldn't upload those photos.");
      const cleanError: CleanError = {
        message,
        code,
        source: "image",
        sourceLabel: label,
      };
      setError(cleanError);
      // Persist the error too so a refresh lands back on the error screen
      // with the same retry context.
      persistAttempt(files, dataUrls, uploadedUrls, cleanError, attempt);
      setLoading(false);
      setLoadingMessage(undefined);
      setUploadProgress(undefined);
      return;
    }

    // All uploads succeeded — narrow to a string[] for the request body.
    const finalUrls = uploadedUrls as string[];
    setLoadingMessage(undefined);
    setUploadProgress(undefined);

    const bestEffort = attempt >= 2;
    const body: Record<string, unknown> =
      finalUrls.length > 1
        ? { images: finalUrls }
        : { image: finalUrls[0] };
    if (bestEffort) body.best_effort = true;

    const { recipe, error: err } = await callClean(body);

    if (recipe) {
      // Photo/PDF flow: always prefer the user's uploaded photo (or rendered
      // PDF page) as the hero. AI-supplied image_url values from photo/PDF
      // inputs are unreliable (often hallucinated from text in the image),
      // so we demote them into the candidates list instead.
      const aiUrl = recipe.image_url;
      if (dataUrls[0]) {
        recipe.image_url = dataUrls[0];
      }
      const extraCandidates: { url: string; source: string }[] = [];
      if (aiUrl && aiUrl !== recipe.image_url) {
        extraCandidates.push({ url: aiUrl, source: "ai" });
      }
      if (dataUrls.length > 1) {
        for (const url of dataUrls) {
          extraCandidates.push({ url, source: "upload" });
        }
      }
      if (extraCandidates.length > 0) {
        recipe.image_candidates = [
          ...(recipe.image_candidates || []),
          ...extraCandidates,
        ];
      }
      goToRecipe(recipe);
      return;
    }
    if (bestEffort) {
      goToRecipe(stubRecipe());
      return;
    }
    const label =
      files.length === 1
        ? files[0].name
        : `${files.length} photos`;
    setError({ ...err!, source: "image", sourceLabel: label });
    setLoading(false);
  };

  /**
   * Validate + convert a batch of incoming files (images or PDFs) into staged
   * items. Returns null and toasts on failure.
   */
  const filesToStaged = async (filesIn: File[]): Promise<StagedItem[] | null> => {
    const accepted: File[] = [];
    const pdfs: File[] = [];
    for (const f of filesIn) {
      if (f.type === "application/pdf" || /\.pdf$/i.test(f.name)) {
        pdfs.push(f);
      } else if (f.type.startsWith("image/")) {
        accepted.push(f);
      }
    }
    if (accepted.length === 0 && pdfs.length === 0) {
      toast.error("Please choose images or a PDF");
      return null;
    }

    const tooBig = accepted.find((f) => f.size > MAX_IMAGE_BYTES);
    if (tooBig) {
      toast.error(`"${tooBig.name}" is over 8MB. Please choose smaller images.`);
      return null;
    }

    const staged: StagedItem[] = [];

    try {
      for (const f of accepted) {
        const processed = await processImage(f);
        const dataUrl = await blobToDataUrl(processed);
        const prepared = new File([processed], f.name.replace(/\.[^.]+$/, "") + ".jpg", {
          type: "image/jpeg",
        });
        staged.push({ id: newId(), file: prepared, dataUrl });
      }
    } catch {
      toast.error("Couldn't read those images. Please try again.");
      return null;
    }

    if (pdfs.length > 0) {
      setLoadingMessage("Converting PDF…");
      setLoading(true);
      let pdfPagesAdded = 0;
      let pdfFailures = 0;
      try {
        const { pdfToImages } = await import("@/lib/pdfToImages");
        for (const pdf of pdfs) {
          try {
            const { pages, truncated } = await pdfToImages(pdf, MAX_IMAGES);
            if (truncated) {
              toast.message(
                `Only the first ${MAX_IMAGES} pages of "${pdf.name}" were used. Drag to reorder if needed.`,
              );
            }
            for (const p of pages) {
              staged.push({ id: newId(), file: p.file, dataUrl: p.dataUrl });
              pdfPagesAdded += 1;
            }
          } catch (err) {
            console.error("pdfToImages failed", err);
            pdfFailures += 1;
            toast.error(`Couldn't read "${pdf.name}" as a PDF.`);
          }
        }
      } finally {
        setLoading(false);
        setLoadingMessage(undefined);
      }

      // If we had PDFs but extracted zero pages and have no other images,
      // surface a clear error and bail so the home screen doesn't silently reset.
      if (pdfPagesAdded === 0 && accepted.length === 0) {
        if (pdfFailures > 0) {
          toast.error(
            "Couldn't read that PDF — try uploading screenshots of the pages instead.",
          );
        }
        return null;
      }
    }

    return staged;
  };

  /**
   * Entry point for any media (images or PDFs). Single image → straight to AI.
   * Multiple items → route into the PhotoTray to confirm order first.
   * Camera-originated batches always route into the tray so the user can
   * take additional photos before cleaning.
   */
  const startMediaFlow = async (filesIn: File[], fromCamera = false) => {
    const items = await filesToStaged(filesIn);
    if (!items) return;
    if (items.length === 0 && staged.length === 0) return;

    const combined = [...staged, ...items].slice(0, MAX_IMAGES);
    if (staged.length + items.length > MAX_IMAGES) {
      toast.message(`Trimmed to ${MAX_IMAGES} pages.`);
    }

    const isCameraBatch = fromCamera || cameraBatch;
    if (combined.length === 1 && !isCameraBatch) {
      cleanImage([combined[0].file], [combined[0].dataUrl], 0);
      setStaged([]);
      setCameraBatch(false);
      return;
    }
    if (fromCamera) setCameraBatch(true);
    setStaged(combined);
  };

  const handleRetry = () => {
    if (!lastAttempt) return;
    const next = retryCount + 1;
    if (lastAttempt.type === "url") {
      cleanUrl(lastAttempt.url, next);
    } else if (lastAttempt.type === "text") {
      cleanText(lastAttempt.text, next);
    } else {
      cleanImage(
        lastAttempt.files,
        lastAttempt.dataUrls,
        next,
        lastAttempt.uploadedUrls,
      );
    }
  };

  const handleStartOver = () => {
    setError(null);
    setLastAttempt(null);
    setRetryCount(0);
    setCropImage(null);
    setStaged([]);
    setCameraBatch(false);
    clearPersistedAttempt();
    if (fileRef.current) fileRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
    if (pdfRef.current) pdfRef.current.value = "";
  };

  const handleMarkRegions = async () => {
    if (!lastAttempt) return;

    if (lastAttempt.type === "image") {
      setCropImage(lastAttempt.dataUrls[0]);
      return;
    }
    if (lastAttempt.type === "text") {
      // No screenshot to mark regions on for text input — just bail.
      return;
    }

    setLoading(true);
    setLoadingMessage("Grabbing a screenshot…");
    setError(null);

    const { data, error: invokeError } = await supabase.functions.invoke(
      "clean-recipe",
      { body: { url: lastAttempt.url, screenshot_only: true } },
    );

    if (invokeError || data?.error || !data?.screenshot) {
      const { message } = invokeError
        ? await parseFunctionError(invokeError)
        : { message: data?.error ?? "Couldn't capture a screenshot." };
      toast.error(message || "Couldn't capture a screenshot — opening an editable draft instead.");
      goToRecipe(stubRecipe(lastAttempt.url));
      return;
    }

    setLoading(false);
    setLoadingMessage(undefined);
    setCropImage(data.screenshot as string);
  };

  const handleCropComplete = async (crops: {
    title: string[];
    ingredients: string[];
    method: string[];
  }) => {
    setCropImage(null);
    const total = crops.title.length + crops.ingredients.length + crops.method.length;
    if (total === 0) {
      toast.error("Please draw at least one box.");
      return;
    }
    setLoading(true);
    setLoadingMessage("Reading the regions you marked…");
    setError(null);

    const { recipe, error: err } = await callClean({ image_regions: crops });

    if (recipe) {
      goToRecipe(recipe);
      return;
    }
    toast.error(err?.message || "Still couldn't read it — opening an editable draft.");
    const fallbackUrl = lastAttempt?.type === "url" ? lastAttempt.url : undefined;
    goToRecipe(stubRecipe(fallbackUrl));
  };

  const handleCropCancel = () => {
    setCropImage(null);
  };

  const handleUploadFromError = (accept: "image" | "pdf" | "any") => {
    setError(null);
    if (accept === "pdf") pdfRef.current?.click();
    else fileRef.current?.click();
  };

  /**
   * "Paste the recipe text instead" recovery action. Clears the error,
   * forces the RecipeInput onto its text tab, and scrolls back to the
   * capture surface so the textarea is in view ready to accept a paste.
   */
  const handlePasteFromError = () => {
    setError(null);
    setLastAttempt(null);
    setRetryCount(0);
    setForcedMode("text");
    // Wait a frame for the home view to mount, then scroll + focus.
    setTimeout(() => {
      scrollToHero();
    }, 50);
  };
  // Hidden file inputs are rendered ONCE at the top of every view so their
  // refs stay attached to live DOM nodes regardless of which view is active.
  // This is critical on iOS Safari: the PhotoTray's "Take photo" button calls
  // cameraRef.current?.click() inside a user-gesture handler — if the input
  // isn't in the DOM (e.g. it lived inside the home view that we conditionally
  // returned away from), the ref is null and the click silently no-ops.
  const hiddenInputs = (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          const fs = Array.from(e.target.files ?? []);
          if (fs.length > 0) startMediaFlow(fs);
          e.target.value = "";
        }}
      />
      <input
        ref={pdfRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const fs = Array.from(e.target.files ?? []);
          if (fs.length > 0) startMediaFlow(fs);
          e.target.value = "";
        }}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const fs = Array.from(e.target.files ?? []);
          if (fs.length > 0) startMediaFlow(fs, true);
          e.target.value = "";
        }}
      />
    </>
  );

  /* ----- PhotoTray view ----- */
  if (staged.length > 0 && !loading && !cropImage && !error) {
    const items: TrayItem[] = staged.map((s) => ({
      id: s.id,
      preview: s.dataUrl,
      name: s.file.name,
    }));
    return (
      <main className="min-h-screen bg-warm-gradient">
        {hiddenInputs}
        <AppHeader logoStatic hideCleanAnother />
        <PhotoTray
          items={items}
          maxItems={MAX_IMAGES}
          onReorder={(next) => {
            const map = new Map(staged.map((s) => [s.id, s]));
            setStaged(next.map((n) => map.get(n.id)!).filter(Boolean));
          }}
          onRemove={(id) => setStaged((prev) => prev.filter((s) => s.id !== id))}
          onAddMorePhoto={() => cameraRef.current?.click()}
          onAddMoreUpload={() => fileRef.current?.click()}
          showCameraButton={mobile}
          onClear={() => {
            setStaged([]);
            setCameraBatch(false);
          }}
          onConfirm={() => {
            const files = staged.map((s) => s.file);
            const dataUrls = staged.map((s) => s.dataUrl);
            setStaged([]);
            setCameraBatch(false);
            cleanImage(files, dataUrls, 0);
          }}
        />
      </main>
    );
  }

  if (cropImage) {
    return (
      <>
        {hiddenInputs}
        <Suspense fallback={null}>
          <GuidedCrop
            imageDataUrl={cropImage}
            onComplete={handleCropComplete}
            onCancel={handleCropCancel}
          />
        </Suspense>
      </>
    );
  }

  if (loading) {
    // First-time users get the friendlier onboarding copy described in
    // Step 4 of the plan; repeat users see the existing rotating messages.
    const firstTime = !hasFlag("firstClean");
    return (
      <main className="min-h-screen bg-warm-gradient">
        {hiddenInputs}
        <AppHeader hideCleanAnother />
        <LoadingState
          message={loadingMessage ?? (firstTime ? "Organising your recipe…" : undefined)}
          subMessage={firstTime ? "Turning this into a clean, usable format" : undefined}
          progress={uploadProgress}
        />
      </main>
    );
  }

  if (error) {
    const showRegions = retryCount >= 1;
    return (
      <main className="min-h-screen bg-warm-gradient">
        {hiddenInputs}
        <AppHeader logoStatic hideCleanAnother />
        <ErrorState
          error={error}
          onRetry={handleRetry}
          onStartOver={handleStartOver}
          onMarkRegions={showRegions ? handleMarkRegions : undefined}
          onUploadFiles={
            error.source === "url" ? handleUploadFromError : undefined
          }
          onPasteText={
            error.source === "url" ? handlePasteFromError : undefined
          }
        />
      </main>
    );
  }

  // Just-onboarded anonymous users get a slightly more directive headline
  // ("Add your first recipe") on the capture surface — see Step 3 of the
  // onboarding plan. Returning anon users keep the original landing copy.
  const justOnboarded = !signedIn && hasFlag("onboarded") && !hasFlag("firstClean");

  return (
    <main className="min-h-screen bg-warm-gradient">
      {hiddenInputs}
      <AppHeader logoStatic hideCleanAnother />

      {showWelcome && signedIn === false && !captureMode && (
        <WelcomeHero
          onGetStarted={() => {
            setShowWelcome(false);
            setFlag("onboarded");
            // Wait for the capture section to mount before scrolling.
            setTimeout(() => scrollToHero(), 60);
          }}
        />
      )}

      <section
        ref={heroRef}
        className="mx-auto max-w-6xl px-6 pb-20 pt-10 sm:pt-16 md:pt-20"
      >
        {captureMode || signedIn ? (
          <div className="text-center animate-fade-up">
            <h1 className="font-display text-3xl font-semibold leading-tight text-foreground sm:text-4xl">
              Clean another recipe
            </h1>
            <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground sm:text-base">
              Upload a photo, paste a link, or drop in some notes — we&rsquo;ll
              tidy it up for you.
            </p>
          </div>
        ) : justOnboarded ? (
          // Just-onboarded anonymous user: focused, action-oriented header
          // that mirrors Step 3 of the onboarding plan ("Add your first
          // recipe"). The capture controls render directly below.
          <div className="text-center animate-fade-up">
            <h1 className="font-display text-4xl font-semibold leading-tight text-foreground sm:text-5xl">
              Add your first recipe
            </h1>
            <p className="mx-auto mt-3 max-w-md text-base text-muted-foreground sm:text-lg">
              Start with anything you already have — a photo, a link, or some
              text.
            </p>
          </div>
        ) : (
          <div className="grid items-center gap-12 md:grid-cols-[1.1fr_1fr] md:gap-10">
            <div className="text-center md:text-left animate-fade-up">
              <p className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-mustard/20 px-3 py-1 text-xs font-semibold tracking-wide text-foreground ring-1 ring-mustard/40">
                <span className="h-1.5 w-1.5 rounded-full bg-mustard" />
                No sign-up required
              </p>
              <h1 className="font-display text-4xl font-semibold leading-[1.05] text-foreground sm:text-5xl md:text-6xl">
                Turn your pile of recipes into something you&rsquo;ll actually use
              </h1>
              <p className="mx-auto mt-5 max-w-md text-base text-muted-foreground sm:text-lg md:mx-0">
                Photos, screenshots, links or notes — CleanPlate turns them into
                clean, structured recipes in seconds.
              </p>
            </div>
            <div
              className="animate-fade-up"
              style={{ animationDelay: "180ms" }}
            >
              <HeroVisual />
            </div>
          </div>
        )}

        <div
          className="mt-12 animate-fade-up"
          style={{ animationDelay: "240ms" }}
        >
          <RecipeInput
            headline=""
            subhead=""
            forceMode={forcedMode}
            onSubmitUrl={(u) => {
              setForcedMode(undefined);
              cleanUrl(u, 0);
            }}
            onSubmitText={(t) => {
              setForcedMode(undefined);
              cleanText(t, 0);
            }}
            onSubmitFiles={(files, fromCamera) => {
              setForcedMode(undefined);
              startMediaFlow(files, fromCamera);
            }}
          />
        </div>
      </section>

      {!signedIn && !captureMode && (
        <>
          <Problem />
          <Solution />
          <Showcase />
          <Benefits />
          <SaveOrganise onCta={() => scrollToHero()} />
          <SocialProof />
          <FinalCTA
            onUploadClick={() => {
              scrollToHero();
              setTimeout(() => fileRef.current?.click(), 450);
            }}
            onPasteClick={() => scrollToHero()}
          />
        </>
      )}

      <footer className="border-t border-border/60 py-8 text-center">
        <p className="text-xs text-muted-foreground">
          Made for home cooks. Less clutter, better cooking.
        </p>
      </footer>
    </main>
  );
};

export default Index;
